import { useEffect, useState } from 'react';

interface LocationState {
  pathname: string;
  search: string;
  hash: string;
  host: string;
  hostname: string;
  port: string;
  protocol: string;
  origin: string;
  href: string;
  isPath: (path: string) => boolean;
  hasPath: (path: string) => boolean;
}

type LocationKey = keyof LocationState;
type WatchFor = LocationKey | 'all';

const defaultValues = {
  pathname: '',
  search: '',
  hash: '',
  host: '',
  hostname: '',
  port: '',
  protocol: '',
  origin: '',
  href: '',
  isPath: (value: string) => false,
  hasPath: (value: string) => false
} as LocationState;

const useLocation = (watchFor: WatchFor = 'all') => {
  const [locationState, setLocationState] = useState<LocationState>(() => {
    if (typeof window === 'undefined') {return defaultValues;}
    return {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      host: window.location.host,
      hostname: window.location.hostname,
      port: window.location.port,
      protocol: window.location.protocol,
      origin: window.location.origin,
      href: window.location.href,
      isPath: (path: string) => window.location.pathname.toLowerCase() === path,
      hasPath: (path: string) => window.location.pathname.toLowerCase().includes(path)
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') {return;}

    const handleLocationChange = () => {
      const newState: LocationState = {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        host: window.location.host,
        hostname: window.location.hostname,
        port: window.location.port,
        protocol: window.location.protocol,
        origin: window.location.origin,
        href: window.location.href,
        isPath: (path: string) => window.location.pathname.toLowerCase() === path,
        hasPath: (path: string) => window.location.pathname.toLowerCase().includes(path)
      };

      if (watchFor === 'all' || newState[watchFor] !== locationState[watchFor]) {
        setLocationState(newState);
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('replaceState', handleLocationChange);

    // Override pushState and replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(data: any, unused: string, url?: string | URL | null) {
      originalPushState.call(this, data, unused, url);
      handleLocationChange();
    };

    window.history.replaceState = function(data: any, unused: string, url?: string | URL | null) {
      originalReplaceState.call(this, data, unused, url);
      handleLocationChange();
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [watchFor, locationState]);

  return locationState;
};

export default useLocation;
